import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useLanguage } from '../components/LanguageContext';
import { CHARACTERS, CATEGORIES, GENDERS, SURPRISE_ID } from '../components/data/characters';
import SurpriseMeTile from '../components/photobooth/SurpriseMeTile';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Camera, ArrowRight, ChevronLeft, ChevronRight, Wand2, Users, User } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

export default function Gallery() {
  const { t, language, isRTL } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGender, setActiveGender] = useState('male');

  // Filter characters based on search and gender
  const filteredCharacters = CHARACTERS.filter(char => {
    const matchesGender = activeGender === 'all' || char.gender === activeGender || char.gender === 'all' || char.gender === 'neutral';
    const matchesSearch = searchQuery === '' || 
      char.name_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      char.name_he.includes(searchQuery);
    return matchesGender && matchesSearch;
  });

  // Group characters by category
  const groupedCharacters = CATEGORIES.filter(cat => cat.id !== 'all').reduce((acc, category) => {
    const chars = filteredCharacters.filter(char => char.category === category.id);
    if (chars.length > 0) {
      acc.push({ category, characters: chars });
    }
    return acc;
  }, []);

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
          >
            {language === 'he' ? 'גלריית הדמויות' : 'Character Gallery'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-400 max-w-2xl mx-auto"
          >
            {language === 'he' 
              ? 'חקור את 80+ הדמויות שלנו ובחר את הבאה שלך'
              : 'Explore our 80+ characters and choose your next transformation'}
          </motion.p>
        </div>

        {/* Custom Character + Gender Filter + Search */}
        <div className="glass-card rounded-2xl p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Custom Style Button */}
            <Link to={createPageUrl('CustomCharacter')}>
              <Button className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-6 h-12 rounded-xl">
                <Wand2 className="w-5 h-5 me-2" />
                {language === 'he' ? 'דמות מותאמת אישית' : 'Custom Style'}
              </Button>
            </Link>

            {/* Gender Toggle */}
            <div className="flex gap-2 p-1 bg-slate-800/50 rounded-xl">
              {GENDERS.map((gender) => (
                <button
                  key={gender.id}
                  onClick={() => setActiveGender(gender.id)}
                  className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    activeGender === gender.id
                      ? 'bg-violet-600 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                  }`}
                >
                  {gender.id === 'male' && <User className="w-4 h-4" />}
                  {gender.id === 'female' && <Users className="w-4 h-4" />}
                  {language === 'he' ? gender.name_he : gender.name_en}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative w-full lg:w-80">
              <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 ${isRTL ? 'right-4' : 'left-4'}`} />
              <Input
                type="text"
                placeholder={language === 'he' ? 'חפש דמות...' : 'Search styles...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 h-12 ${isRTL ? 'pr-12 text-right' : 'pl-12'}`}
              />
            </div>
          </div>
        </div>

        {/* Surprise Me — the same tile the booth shows. It opens the booth with the
            tile pre-selected; the actual character is rolled at generate time. */}
        <div className="mb-8">
          <SurpriseMeTile
            onClick={() => window.location.href = `${createPageUrl('Photobooth')}?character=${SURPRISE_ID}`}
          />
        </div>

        {/* Characters by Category */}
        <div className="space-y-10">
          {groupedCharacters.map(({ category, characters }) => (
            <CategorySection
              key={category.id}
              category={category}
              characters={characters}
              language={language}
              isRTL={isRTL}
              onSelectCharacter={(char) => window.location.href = `${createPageUrl('Photobooth')}?character=${char.id}`}
            />
          ))}
        </div>

        {/* No Results */}
        {groupedCharacters.length === 0 && (
          <div className="text-center py-20">
            <p className="text-slate-400 text-lg">
              {language === 'he' ? 'לא נמצאו דמויות' : 'No characters found'}
            </p>
          </div>
        )}

      </div>
    </div>
  );
}

function CategorySection({ category, characters, language, isRTL, onSelectCharacter }) {
  const [page, setPage] = useState(0);

  React.useEffect(() => {
    setPage(0);
  }, [characters.length]);

  const perPage = 10;
  const totalPages = Math.max(1, Math.ceil(characters.length / perPage));
  const startIndex = page * perPage;
  const visibleCharacters = characters.slice(startIndex, startIndex + perPage);

  const canGoLeft = page > 0;
  const canGoRight = page < totalPages - 1;

  const goToPage = (direction) => {
    setPage((current) => {
      if (direction === 'left') {
        return current > 0 ? current - 1 : 0;
      }
      if (direction === 'right') {
        return current < totalPages - 1 ? current + 1 : current;
      }
      return current;
    });
  };

  return (
    <div className="relative">
      {/* Category Header */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-white">
          {language === 'he' ? category.name_he : category.name_en}
        </h2>
        <span className="text-sm text-slate-500">({characters.length})</span>
      </div>

      {/* Scroll / Paging Container */}
      <div className="relative group">
        {/* Left Arrow */}
        <button
          type="button"
          disabled={!canGoLeft}
          onClick={() => canGoLeft && goToPage('left')}
          className={`absolute ${isRTL ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all
            ${canGoLeft
              ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-500 hover:scale-110'
              : 'bg-gray-700 border-gray-600 text-gray-500 cursor-default'}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Right Arrow */}
        <button
          type="button"
          disabled={!canGoRight}
          onClick={() => canGoRight && goToPage('right')}
          className={`absolute ${isRTL ? 'left-0' : 'right-0'} top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all
            ${canGoRight
              ? 'bg-blue-600 border-blue-500 text-white hover:bg-blue-500 hover:scale-110'
              : 'bg-gray-700 border-gray-600 text-gray-500 cursor-default'}`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Characters Row */}
        <div className="flex gap-4 overflow-hidden pb-4">
          {visibleCharacters.map((char) => (
            <motion.button
              key={char.id}
              whileHover={{ scale: 1.02 }}
              onClick={() => onSelectCharacter(char)}
              className="flex-shrink-0 w-40 group relative rounded-xl overflow-hidden bg-slate-800"
            >
              <div className="aspect-[3/4]">
                <img
                  src={char.image_url}
                  alt={language === 'he' ? char.name_he : char.name_en}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <span className="text-sm font-medium text-white">
                  {language === 'he' ? char.name_he : char.name_en}
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Page Indicator */}
        {totalPages > 1 && (
          <div className="flex justify-end mt-1">
            <span className="text-xs text-slate-500">
              {language === 'he'
                ? `עמוד ${page + 1} מתוך ${totalPages}`
                : `Page ${page + 1} of ${totalPages}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
